import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import { Footer, Navbar } from '@/components/layouts';
import { KanbanHeader } from '@/components/layouts/kanban';
import TraceIconSpinner from '@/components/ui/traceIconSpinner';
import { UserDashboardHeader } from '@/components/userDashboard/userDashboardHeader/userDashboardHeader';
import { TOOLTIP_PORTAL } from '@/constant/htmlIds';
import {
  WORKFLOW_CARDS_DASHBOARD_QUERY,
  type WorkflowCardsDashboardResult
} from '@/gql/queries/workflow-cards.gql';
import { useUser } from '@/hooks/useUser';
import { setWorkspaceHighlightOverride } from '@/hooks/useWorkspaceHighlight';
import GroupsTaskList from './groupsTaskList';
import {
  getUserDisplayConfig,
  saveUserDisplayConfig
} from './userDisplayConfig';
import { useWorkspaceFilterSelection } from './workflowsCards/useWorkspaceFilterSelection';
import { WorkflowsCards } from './workflowsCards/WorkflowsCards';
import { WorkspaceFilter } from './workflowsCards/WorkspaceFilter';
import { DEFAULT_WORKSPACE_FILTER } from './workflowsCards/workflowCards.schema';
import {
  getQualifyingWorkspaces,
  matchesWorkspaceSelection,
  type WorkflowCardItem
} from './workflowsCards/workspaceFilterMatch';

const CARD_SECTION_CLASS = 'm-2 rounded-2xl border border-border';

export const WorkflowCardsDashboard = memo(() => {
  useEffect(() => {
    document.title = 'Trace';
  }, []);

  const { user, isPrivileged, loading: userLoading } = useUser();
  const { data, loading: dataLoading } = useQuery<WorkflowCardsDashboardResult>(
    WORKFLOW_CARDS_DASHBOARD_QUERY,
    {
      fetchPolicy: 'cache-and-network'
    }
  );

  const [displayConfig, setDisplayConfig] = useState(() =>
    getUserDisplayConfig()
  );
  const collapsedHeader = displayConfig.collapsedHeader ?? false;

  const updateDisplayConfig = useCallback(
    (overlay: Record<string, unknown>) => {
      setDisplayConfig(prev => {
        const next = { ...prev, ...overlay };
        saveUserDisplayConfig(next);
        return next;
      });
    },
    []
  );
  const setCollapsedHeader = useCallback(
    (value: boolean) => updateDisplayConfig({ collapsedHeader: value }),
    [updateDisplayConfig]
  );
  const setGroupsConfig = useCallback(
    (value: unknown) => updateDisplayConfig({ groupsTaskList: value }),
    [updateDisplayConfig]
  );

  const userId = user ? String(user.rowId) : '';
  const [selection, setSelection] = useWorkspaceFilterSelection(
    userId,
    Boolean(user)
  );

  const workflows: WorkflowCardItem[] = useMemo(
    () => data?.workflows?.nodes ?? [],
    [data]
  );
  const qualifyingWorkspaces = useMemo(
    () => getQualifyingWorkspaces(workflows),
    [workflows]
  );
  const filteredWorkflows = useMemo(
    () => workflows.filter(w => matchesWorkspaceSelection(w, selection)),
    [workflows, selection]
  );

  // Reconcile a persisted workspace selection that no longer matches any
  // visible workspace (e.g. the workspace was deleted or access was revoked):
  // fall back to Production rather than silently filtering everything out while
  // the trigger keeps showing a stale/empty state. Guarded on loaded data so we
  // don't reset during the initial fetch.
  useEffect(() => {
    if (
      dataLoading ||
      selection.kind !== 'workspace' ||
      workflows.length === 0
    ) {
      return;
    }
    const stillExists = qualifyingWorkspaces.some(
      w => w.rowId === selection.id
    );
    if (!stillExists) {
      setSelection(DEFAULT_WORKSPACE_FILTER);
    }
  }, [
    dataLoading,
    selection,
    qualifyingWorkspaces,
    workflows.length,
    setSelection
  ]);

  // Tint the screen-edge border and the navbar highlight with the selected
  // workspace's color: published to the shared highlight store, where the
  // root-mounted RouteWorkspaceBorder renders the frame and the navbar reads
  // its wash.
  const selectedWorkspaceColor = useMemo(() => {
    if (selection.kind !== 'workspace') {
      return null;
    }
    return (
      qualifyingWorkspaces.find(w => w.rowId === selection.id)?.color ?? null
    );
  }, [selection, qualifyingWorkspaces]);
  useEffect(() => {
    setWorkspaceHighlightOverride(selectedWorkspaceColor);
    return () => {
      setWorkspaceHighlightOverride(null);
    };
  }, [selectedWorkspaceColor]);

  const header = useMemo(
    () => (
      <KanbanHeader>
        <Navbar
          centerSlot={
            qualifyingWorkspaces.length > 0 ? (
              <WorkspaceFilter
                onChange={setSelection}
                selection={selection}
                userId={userId}
                workspaces={qualifyingWorkspaces}
              />
            ) : undefined
          }
        />
      </KanbanHeader>
    ),
    [qualifyingWorkspaces, selection, setSelection, userId]
  );

  const loading = userLoading || (dataLoading && !data);

  return (
    <>
      <div id={TOOLTIP_PORTAL} />
      {header}
      {/* Height and right edge also subtract the frame headroom (0 unless the
          screen border is up) so the scroll area — its footer pinned at the
          bottom and its vertical scrollbar at the right — ends above the
          screen border instead of under it. */}
      <div className="fixed top-navbar right-[var(--workspace-frame-headroom,0px)] left-0 h-[calc(100%-var(--spacing-navbar)-var(--workspace-frame-headroom,0px))] overflow-x-auto overflow-y-scroll bg-background">
        {loading || !user ? (
          <TraceIconSpinner />
        ) : (
          <div className="relative flex flex-col flex-nowrap p-4">
            <UserDashboardHeader
              className={CARD_SECTION_CLASS}
              collapsed={collapsedHeader}
              setCollapsed={setCollapsedHeader}
            />
            <WorkflowsCards
              className="m-2"
              isPrivileged={isPrivileged}
              userId={userId}
              workflows={filteredWorkflows}
            />
            <GroupsTaskList
              className={CARD_SECTION_CLASS}
              config={displayConfig.groupsTaskList ?? {}}
              setConfig={setGroupsConfig}
              workflows={filteredWorkflows}
            />
            <Footer />
          </div>
        )}
      </div>
    </>
  );
});
